import { forwardRef, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';
import { UsersApiAdapter } from '../../adapters/UsersApiAdapter';
import { useRoles } from '../../hooks/useRoles';
import { UserModel } from '../../models/User';
import { useAuthStore } from '@shared/stores/authStore';

interface ChangeRoleModalProps {
  user: UserModel | null;
  onSuccess?: () => void;
}

/**
 * Modal para cambiar el rol de un usuario
 * Muestra la información del usuario y permite seleccionar un nuevo rol
 */
export const ChangeRoleModal = forwardRef<ModalCenterRef, ChangeRoleModalProps>(({ user, onSuccess }, ref) => {
  const { t } = useTranslation('usersModule');
  const { toast } = useToast();
  const { roles, isLoading: isLoadingRoles } = useRoles();
  const { team } = useAuthStore();

  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isChanging, setIsChanging] = useState(false);

  // 🎯 Inicializar el adapter para operaciones de usuario
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const usersAdapter = useMemo(() => new UsersApiAdapter(httpClient), [httpClient]);

  // 🎯 Convertir roles a formato de opciones para el Combobox
  const roleOptions = useMemo(() => {
    return roles.map((role) => ({
      label: role.name,
      value: role.id.toString(),
    }));
  }, [roles]);

  // 🎯 Establecer el rol actual del usuario cuando el modal se abre
  useEffect(() => {
    if (user?.current_role_id) {
      setSelectedRoleId(user.current_role_id.toString());
    } else {
      setSelectedRoleId('');
    }
  }, [user]);

  // 🎯 Limpiar formulario
  const resetForm = () => {
    if (user?.current_role_id) {
      setSelectedRoleId(user.current_role_id.toString());
    } else {
      setSelectedRoleId('');
    }
  };

  // 🎯 Manejar cierre del modal
  const handleClose = () => {
    resetForm();
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
  };

  // 🎯 Manejar cambio de rol
  const handleChangeRole = async () => {
    if (!user) {
      return;
    }

    // Validar que se haya seleccionado un rol
    if (!selectedRoleId) {
      toast({
        title: t('modals.changeRole.errors.roleRequired'),
        description: t('modals.changeRole.errors.roleRequiredDesc'),
        variant: 'destructive',
      });
      return;
    }

    // Validar que el rol sea diferente al actual
    if (selectedRoleId === user.current_role_id?.toString()) {
      toast({
        title: t('modals.changeRole.errors.sameRole'),
        description: t('modals.changeRole.errors.sameRoleDesc'),
        variant: 'destructive',
      });
      return;
    }

    // Validar que exista el team
    if (!team?.id) {
      toast({
        title: t('modals.changeRole.errors.teamRequired'),
        description: t('modals.changeRole.errors.teamRequiredDesc'),
        variant: 'destructive',
      });
      return;
    }

    setIsChanging(true);

    try {
      const response = await usersAdapter.changeUserRole(team.id, user.id, parseInt(selectedRoleId));

      // 🎯 Si la respuesta es exitosa (200 OK), mostrar toast de éxito
      if (response.success) {
        toast({
          title: t('modals.changeRole.success.title'),
          description: response.message || t('modals.changeRole.success.description'),
          variant: 'default',
        });
      } else {
        // 🎯 Si hay error, mostrar el mensaje de error de la API
        toast({
          title: t('modals.changeRole.errors.title'),
          description: response.message || t('modals.changeRole.errors.unexpected'),
          variant: 'destructive',
        });
      }

      // 🎯 Cerrar modal y hacer refetch en ambos casos (éxito o error)
      handleClose();
      onSuccess?.();
    } catch (error: any) {
      // 🎯 Extraer mensaje de error como string
      let errorMessage = t('modals.changeRole.errors.unexpected');

      if (error?.response?.data) {
        const errorData = error.response.data;

        // La API devuelve el error en errorData.error.message
        if (
          errorData.error &&
          typeof errorData.error === 'object' &&
          errorData.error.message &&
          typeof errorData.error.message === 'string'
        ) {
          errorMessage = errorData.error.message;
        }
        // También puede venir directamente en errorData.message
        else if (errorData.message && typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
      } else if (error?.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      // 🎯 Mostrar error inesperado
      toast({
        title: t('modals.changeRole.errors.title'),
        description: errorMessage,
        variant: 'destructive',
      });

      // 🎯 Cerrar modal y hacer refetch incluso en caso de error
      handleClose();
      onSuccess?.();
    } finally {
      setIsChanging(false);
    }
  };

  // Si no hay usuario, no renderizar
  if (!user) {
    return null;
  }

  return (
    <ModalCenter
      ref={ref}
      title={t('modals.changeRole.title')}
      type="info"
      width="lg"
      classNameBody="max-h-[calc(100vh-120px)] overflow-y-auto"
      data-testid="change-role-modal"
    >
      <div className="space-y-6">
        {/* Descripción */}
        <p className="text-sm text-complementary-text">{t('modals.changeRole.description')}</p>

        {/* Información del usuario */}
        <div className="space-y-2">
          <div>
            <p className="text-base font-medium text-text">{user.name}</p>
            <p className="text-sm text-primary">{user.email}</p>
          </div>
        </div>

        {/* Campo de rol */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.changeRole.fields.role')} *</label>
          <Combobox
            options={roleOptions}
            value={selectedRoleId}
            setValue={setSelectedRoleId}
            placeholder={
              isLoadingRoles ? t('modals.changeRole.fields.roleLoading') : t('modals.changeRole.fields.rolePlaceholder')
            }
            disabled={isChanging || isLoadingRoles}
            dataTestId="change-role-select"
          />
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isChanging} data-testid="change-role-cancel-button">
            {t('modals.changeRole.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleChangeRole}
            disabled={
              isChanging ||
              isLoadingRoles ||
              !selectedRoleId ||
              selectedRoleId === user?.current_role_id?.toString() ||
              !team?.id
            }
            data-testid="change-role-save-button"
            size={'lg'}
          >
            {isChanging ? t('modals.changeRole.buttons.saving') : t('modals.changeRole.buttons.save')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ChangeRoleModal.displayName = 'ChangeRoleModal';
